import { ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { BehaviorSubject } from 'rxjs';
import { ItemPickerOption } from '../item-picker/models/itemPickerOption';

@Component({
  selector: 'multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
})
export class MultiselectComponent {
  visible = true;
  separatorKeysCodes: number[] = [ENTER];
  @Input() isTouched = false;

  @Input() orderByName = false;
  formCtrl = new UntypedFormControl();
  filteredItems: BehaviorSubject<MultiselectItem[]> = new BehaviorSubject<MultiselectItem[]>([]);
  @Input() transparent = false;
  @Input() fixedPlaceholder = false;
  @Input() errorMessage: string;
  @Input() required = false;
  @Input() filterMode = true;
  @Input() removable = true;
  @Input() disabled = false;
  @Input() allowAddNewItem = false;
  @Input() ariaLabelledby: string;
  @Input() readonly = false;
  @Output() selectionChanged = new EventEmitter<MultiselectEvent>();
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autoTrigger: MatAutocompleteTrigger;

  private _items: MultiselectItem[] = [];
  private _placeholder = '';
  @Input() set items(value: MultiselectItem[]) {
    if (value) {
      value
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((v) => {
          if (this._items.findIndex((i) => i.id == v.id) == -1) {
            this._items.push(v);
          }
        });
    }
    this._items.forEach((i) => {
      if (value.findIndex((v) => v.id == i.id) == -1) {
        const index = this._items.indexOf(i);
        this._items.splice(index, 1);
      }
    });
    this.setFilteredItems(this._items);
  }
  get items(): MultiselectItem[] {
    return this._items;
  }

  get orderByValue() {
    return this.orderByName ? 'name' : '';
  }
  @Input() set placeholder(value: string) {
    this._placeholder = value;
  }
  get placeholder(): string {
    if (this.required && this._placeholder) {
      return this.selectedItems.length == 0 ? this._placeholder : this._getPlaceholder();
    } else if (this.required && !this._placeholder) {
      return this.selectedItems.length == 0 ? 'Required' : this._getPlaceholder();
    } else {
      return this.selectedItems.length == 0 ? this._placeholder : this._getPlaceholder();
    }
  }
  private _getPlaceholder() {
    return this.transparent || this.fixedPlaceholder ? this._placeholder : '';
  }

  get selectedItems(): MultiselectItem[] {
    return this._items.filter((i) => i.selected == true);
  }

  @Input() set selectedItems(value) {
    if (this._items.length > 0) {
      this._items.forEach((i) => {
        if (value.findIndex((t) => t.id == i.id) != -1) {
          i.selected = true;
        } else {
          i.selected = false;
        }
      });
    }
  }

  constructor() {
    this.formCtrl.valueChanges.subscribe(async (value) => {
      if (value) {
        await this.setFilteredItems(this._filter(value));
      } else {
        await this.setFilteredItems(this._items);
      }
    });
  }
  private async setFilteredItems(items: MultiselectItem[]) {
    if (!items) return;
    this.filteredItems.next([]); // Clear the list

    // Delay function
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const filteredItems: MultiselectItem[] = [];
    this.filteredItems.next(filteredItems); // Emit updated list

    // Create a lazy-loaded list with a delay for eacn 10 items if we have more than 100 items
    let i = 0;
    for (const item of items) {
      i++;
      filteredItems.push(item);
      if (items.length > 100 && i > 10) {
        await delay(0); // Introduce a slight delay
        i = 0;
      }
    }
  }

  get hasError() {
    return (
      this.required && (this.formCtrl.touched || this.isTouched) && this.selectedItems.length == 0
    );
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;
    // Add our item
    if ((value || '').trim()) {
      let item = this._items.find((x) => x.name.toLowerCase() == value.toLowerCase().trim());
      if (!item) {
        if (this.allowAddNewItem) {
          item = new MultiselectItem(value, value, true);
        } else {
          return;
        }
      }
      this._addToSelection(item);
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }

    this.formCtrl.setValue(null);
  }

  remove(item: MultiselectItem): void {
    item.selected = false;
    this.selectionChanged.emit({
      changedValue: item,
      eventType: MultiselectEventType.Remove,
      selectedItems: this.selectedItems,
    });
    setTimeout(() => {
      this.autoTrigger.openPanel();
    }, 0);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this._addToSelection(event.option.value);
    this.input.nativeElement.value = '';
    this.formCtrl.setValue(null);
  }
  private _addToSelection(item: MultiselectItem) {
    item.selected = true;
    this.selectionChanged.emit({
      changedValue: item,
      eventType: MultiselectEventType.Add,
      selectedItems: this.selectedItems,
    });
    setTimeout(() => {
      this.autoTrigger.openPanel();
    }, 0);
  }

  private _filter(value: string): MultiselectItem[] {
    if (typeof value != 'string') return;
    const filterValue = value.toLowerCase();
    return this._items.filter((item) => item.name.toLowerCase().indexOf(filterValue) != -1);
  }
}

export class MultiselectItem implements ItemPickerOption {
  constructor(
    public id: string,
    public name: string,
    public selected: boolean = false,
    public icon = ''
  ) {
    name == null ? (this.name = '') : (this.name = name);
  }
}
export class MultiselectEvent {
  public changedValue: MultiselectItem;
  public eventType: MultiselectEventType;
  public selectedItems: MultiselectItem[] = [];
}

export enum MultiselectEventType {
  Add,
  Remove,
}
